import React, { useState, useContext } from 'react'
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
} from '@mui/material'
import { Refresh, Save } from '@mui/icons-material'
import { useProcessCostCodes, useMutateAiParsedCostCodes } from './hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetProject } from '../hooks'
import { BackButton } from '../../../components/navigation/BackButton'
import { UploaderComponent } from '../../../components/uploaders/UploaderComponent'
import {
  DialogComponent,
  DialogComponentProps,
} from '../../../components/Dialog'
import SnackBarContext from '../../../context/SnackBarContext'
import { getCostCodeVerificationContent } from '../../../utils/costCodeUtil'
import { Fetching } from '../../../components/Fetching'
import { fetchWithTimeoutAndRetry } from '../../../utils/utilities'

export const ImportCostCodes = () => {
  const { id } = useParams() // id = projectId
  const navigate = useNavigate()
  const { mutateAsync: mutateSaveParsedCostCodesAsync } =
    useMutateAiParsedCostCodes('aicostcodes', id)
  const { mutateAsync: mutateProcessCostCodes, isLoading } =
    useProcessCostCodes('costcodes', id)
  const { data } = useGetProject('project', id)
  const [isOpen, setIsOpen] = useState<boolean | undefined>(false)
  const [aiIsOpen, setAiIsOpen] = useState<boolean | undefined>(false)
  const [parsedCostCodes, setParsedCostCodes] = useState<any>([])
  const [simpleCostCodes, setSimpleCostCodes] = useState<any>([])
  const [errorMessage, setErrorMessage] = useState<string | undefined>('')
  const [verificationContent, setVerificationContent] = useState<any>(null)
  const [retryCount, setRetryCount] = useState<number>(0)
  const [hasFailed, setHasFailed] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [aiParseFailed, setAiParseFailed] = useState(false)
  const [confirmSaveWithoutParsing, setConfirmSaveWithoutParsing] =
    useState(false)
  const [error, setError] = useState<{
    message: string
    type: 'empty' | 'other' | null
  }>({ message: '', type: null })

  const dialogProps = {
    title: 'File was not uploaded due to error: ',
    isOpen: isOpen,
    handleConfirmation: () => {
      setIsOpen(false)
    },
    formIsValid: true,
    acceptButtonText: 'Accept',
  } as DialogComponentProps

  const aiDialogProps = {
    isOpen: aiIsOpen,
    formIsValid: true,
    acceptButtonText: 'Verify & Accept',
    showCloseButton: true,
    closeButtonText: 'Cancel',
    handleClose: () => {
      setAiIsOpen(false)
    },
  } as DialogComponentProps

  const { toggleSnackbar } = useContext(SnackBarContext)

  // This function now sends cost codes for Ai Processing.
  const processImportData = async (costCodeData: any) => {
    setRetryCount(0)
    setHasFailed(false)
    setAiParseFailed(false)
    setError({ message: '', type: null })
    try {
      const processedCostCodesResult = await fetchWithTimeoutAndRetry(
        mutateProcessCostCodes,
        [costCodeData],
        {
          timeout: 30000,
          retries: 3,
          retryDelay: 1000,
          onRetry: (attempts: number) => {
            setRetryCount(attempts)
          },
        }
      )

      if (
        processedCostCodesResult &&
        processedCostCodesResult.processedHeirarchy &&
        processedCostCodesResult.processedHeirarchy.length > 0 &&
        processedCostCodesResult.simpleCostCodes &&
        processedCostCodesResult.simpleCostCodes.length > 0
      ) {
        setParsedCostCodes(processedCostCodesResult.processedHeirarchy)
        setSimpleCostCodes(processedCostCodesResult.simpleCostCodes)
        const content = getCostCodeVerificationContent(
          processedCostCodesResult.processedHeirarchy
        )
        setVerificationContent(content)
        setAiIsOpen(true)
      } else {
        setHasFailed(true)
        setAiParseFailed(true)
        setSimpleCostCodes(costCodeData)
        setError({
          message:
            'The processing resulted in empty cost codes. This might be due to an issue with the input data or the AI processing.',
          type: 'empty',
        })
      }
    } catch (error: any) {
      setHasFailed(true)
      setAiParseFailed(true)
      setSimpleCostCodes(costCodeData)
      setError({
        message: `Failed to process cost codes. ${error.message} Please try again or save without parsing.`,
        type: 'other',
      })
    }
  }

  const handleRetry = () => {
    // Retry the process
    processImportData(parsedCostCodes)
  }

  // Only if the user approves of the costs codes, then we save them.
  const saveParsedCostCodes = async (
    costCodeHierarchy: any,
    simpleCostCodes: any
  ) => {
    setIsSubmitting(true)
    const payload = {
      costCodesHierarchy: costCodeHierarchy,
      simpleCostCodes: simpleCostCodes,
    }
    const saveResult = await mutateSaveParsedCostCodesAsync({
      payload: payload,
    })
    if (saveResult) {
      navigate(`/projects/${id}/cost-codes`)
      toggleSnackbar('Cost codes uploaded successfully', 'success')
    }
    setIsSubmitting(false)
  }

  // Failsafe if AI parsing fails to allow user to save without parsing.
  const saveWithoutParsing = async () => {
    const payload = {
      costCodesHierarchy: [], // Empty array for parsed cost codes
      simpleCostCodes: simpleCostCodes,
    }
    try {
      const saveResult = await mutateSaveParsedCostCodesAsync({
        payload: payload,
      })
      if (saveResult) {
        navigate(`/projects/${id}/cost-codes`)
        toggleSnackbar(
          'Cost codes uploaded successfully without parsing',
          'success'
        )
      }
    } catch (error) {
      toggleSnackbar('Failed to save cost codes without parsing', 'error')
      throw error // Re-throw the error so it can be caught in the dialog's handleConfirmation
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <Card sx={{ marginBottom: 2 }}>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 4 }}>
              Hang tight, we are analyzing your cost code data... this could
              take a few minutes.
            </Typography>
            <Fetching />
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
      {isOpen ? (
        <DialogComponent {...dialogProps}>{errorMessage}</DialogComponent>
      ) : null}
      {aiIsOpen && verificationContent ? (
        <DialogComponent
          {...aiDialogProps}
          handleConfirmation={async () => {
            setIsSubmitting(true)
            try {
              await saveParsedCostCodes(parsedCostCodes, simpleCostCodes)
              setAiIsOpen(false)
            } catch (error) {
              // Handle error, maybe show a snackbar
              toggleSnackbar('Failed to save parsed cost codes', 'error')
            } finally {
              setIsSubmitting(false)
            }
          }}
          showCloseButton={true}
          closeButtonText='Cancel'
          formIsValid={true}
          acceptButtonText='Confirm & Save'
          isSubmitting={isSubmitting}>
          <Box>
            <Typography variant='h4'>Analyzed Cost Codes</Typography>
            <Typography variant='body1' paragraph>
              Please verify that we have detected the correct hierarchical
              structure for your cost codes. Here are a couple of examples from
              your data:
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, my: 2 }}>
              <Typography
                variant='body2'
                component='pre'
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                }}>
                {verificationContent.sampleHierarchy}
              </Typography>
            </Box>
            <Typography variant='body2' paragraph sx={{ fontWeight: 'bold' }}>
              Total number of top-level codes:{' '}
              {verificationContent.totalTopLevelCodes} <br />
              Maximum depth of hierarchy: {verificationContent.maxDepth}
            </Typography>
            <Typography variant='body1'>
              Does this hierarchical structure look correct? If yes, please
              confirm by clicking 'Confirm & Save'. If not, please close this
              dialog and re-upload your cost codes.
            </Typography>
          </Box>
        </DialogComponent>
      ) : null}
      {confirmSaveWithoutParsing && (
        <DialogComponent
          title='Save Without Parsing'
          isOpen={confirmSaveWithoutParsing}
          handleConfirmation={async () => {
            setIsSubmitting(true)
            try {
              await saveWithoutParsing()
              setConfirmSaveWithoutParsing(false)
            } catch (error) {
              // Error is already handled in saveWithoutParsing
            } finally {
              setIsSubmitting(false)
            }
          }}
          handleClose={() => setConfirmSaveWithoutParsing(false)}
          formIsValid={true}
          acceptButtonText='Save'
          showCloseButton={true}
          closeButtonText='Cancel'>
          <Typography variant='body1'>
            Are you sure you want to save the cost codes without AI parsing?
            This will save the cost codes in a flat structure without any
            hierarchy.
          </Typography>
        </DialogComponent>
      )}
      <Container maxWidth='lg'>
        {data ? (
          <>
            <BackButton />
            <Typography
              variant='h5'
              gutterBottom>{`${data.projectName} - Import Cost Codes`}</Typography>
          </>
        ) : null}
        <UploaderComponent handleImportData={processImportData} />
        {retryCount > 0 && !hasFailed && (
          <Typography variant='body1'>
            Retry attempt {retryCount} in progress...
          </Typography>
        )}
        {hasFailed && (
          <Box sx={{ mt: 2, mb: 4 }}>
            <Alert
              severity='error'
              action={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant='outlined'
                    color='error'
                    size='small'
                    onClick={handleRetry}
                    disabled={isSubmitting}
                    startIcon={<Refresh />}>
                    Retry
                  </Button>
                  {aiParseFailed && (
                    <Button
                      variant='outlined'
                      color='error'
                      size='small'
                      onClick={() => setConfirmSaveWithoutParsing(true)}
                      disabled={isSubmitting}
                      startIcon={<Save />}>
                      Save Without Parsing
                    </Button>
                  )}
                </Box>
              }
              sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <AlertTitle>Failed to process cost codes</AlertTitle>
              {error.message}
              <Typography variant='caption' display='block' sx={{ mt: 1 }}>
                {error.type === 'empty'
                  ? 'Please check your input file and try again. If the problem persists, you can choose to save without parsing, but this will result in a flat structure without hierarchy.'
                  : 'Note: Saving without parsing will store your cost codes without hierarchical structure.'}
              </Typography>
            </Alert>
          </Box>
        )}
        {isSubmitting && (
          <Box display='flex' alignItems='center' mt={2}>
            <CircularProgress size={24} />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Processing your request...
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  )
}
